import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { UtilityService } from '@eforall/common';
import { GridContentWidget, PageTabsMainTabGridSize } from '@eforall/dashboard';
import { Event } from '@interfaces';
import { DomainDataService } from '../../../../../../services';
import { AcceleratorAreaService } from '../../../accelerator-area.service';
import { AccDeliveryService } from '../../acc-delivery.service';
import { getSetup } from './grid/get-setup';

export interface SurveysBySessionRow {
	accSessionSurveyResponseId: number,
	accSessionAttendeeId: number,
	accSessionId: number,
	eventId: number,
	event: Event,
	sessionStartUTC: number,
	accVentureId: number,
	ventureName: string,
	entrepreneurPersonId: number,
	entrepreneurName: string,
	topicNames: string,
	accessibilityBucket: string,
	communicationBucket: string,
	organizationBucket: string,
	other: string | undefined,
	sessionSurveyAccessibilityScore: number | undefined,
	sessionSurveyCommunicationScore: number | undefined,
	sessionSurveyOrganizationScore: number | undefined,
	createdUTC: number,
	updatedUTC: number,
}

@Component({
	selector: 'acc-surveys-by-session-view-part',
	standalone: true,
	imports: [GridContentWidget],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './surveys-by-session-view.part.html',
})
export class AccSurveysBySessionViewPart {

	private readonly util = inject(UtilityService);
	private readonly pageService = inject(AccDeliveryService);
	private readonly areaService = inject(AcceleratorAreaService);
	private readonly domain = inject(DomainDataService);

	public setup = getSetup(this.util);

	public pageTabsGridSize = computed<PageTabsMainTabGridSize | undefined>(() => {
		if (this.pageService.layout()) return { layout: this.pageService.layout()!, viewSelector: true };
		return undefined;
	});

	public surveysBySession = computed<SurveysBySessionRow[]>(() => {
		const data = this.areaService.data();
		if (!data) return [];


		const events = this.areaService.data()?.events ?? [];

		const ventures = data.ventures;
		const sessions = data.sessions;
		const sessionTopics = data.sessionTopics;
		const accTopics = data.accTopics;
		const sessionAttendees = data.sessionAttendees;
		const sessionSurveyResults = data.sessionSurveyResponses;

		const ddTopics = this.domain.data().topics;


		return sessionSurveyResults.map(survey => {

			const event = events.find(e => e.eventId == survey.eventId)!;
			const accSession = sessions.find(e => e.eventId == event.eventId)!;



			//
			// Get the attendee records for this session
			//
			const attendee = sessionAttendees.find(a => a.accSessionAttendeeId == survey.accSessionAttendeeId && !!a.accVentureId)!;

			const accVenture = ventures.find(venture => venture.accVentureId == attendee.accVentureId)!;

			//
			// Topics
			//
			const accSessionTopics = sessionTopics.filter(sessionTopic => sessionTopic.accSessionId == accSession.accSessionId);
			const accTopicIds = accSessionTopics.map(topic => topic.accTopicId);
			const sessionTopicIds = accTopics.filter(topic => accTopicIds.includes(topic.accTopicId)).map(topic => topic.topicId);


			const row: SurveysBySessionRow = {
				eventId: survey.eventId,
				event,
				accSessionId: accSession.accSessionId,
				topicNames: sessionTopicIds.length ? ddTopics.filter(topic => sessionTopicIds.includes(topic.topicId)).map(topic => topic.name).sort((a, b) => a > b ? 1 : -1).join(', ') : '',
				accSessionAttendeeId: survey.accSessionAttendeeId,
				accSessionSurveyResponseId: survey.accSessionSurveyResponseId,
				sessionStartUTC: event.startUTC,
				accVentureId: accVenture.accVentureId,
				ventureName: accVenture.ventureName,
				entrepreneurPersonId: attendee.personId,
				entrepreneurName: attendee.personName,
				accessibilityBucket: this.util.nps.getBucket(survey.accessibility),
				communicationBucket: this.util.nps.getBucket(survey.communication),
				organizationBucket: this.util.nps.getBucket(survey.organization),
				other: survey.other,
				createdUTC: survey.createdUTC,
				sessionSurveyAccessibilityScore: survey.accessibility,
				sessionSurveyCommunicationScore: survey.communication,
				sessionSurveyOrganizationScore: survey.organization,
				updatedUTC: survey.updatedUTC
			};

			return row;
		});

	});
}